import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { Button } from "./Button";
import { Grid } from "./Grid";
import { ArrowBackOutline, ArrowForwardOutline } from "../icons/icons";
import { SlotsProvider } from "../providers/SlotsProvider";
import { useDefaultProps } from "../providers/utils/defaultProps";
import { mergeSx } from "../system";
export const paginationVariants = ["small", "large"];
export const paginationClasses = {
    root: "MinervaPagination-root",
    "back-button": "MinervaPagination-backButton",
    "next-button": "MinervaPagination-nextButton",
    "page-button": "MinervaPagination-pageButton",
    "page-buttons-container": "MinervaPagination-pageButtonsContainer",
};
export const paginationSlots = {
    backArrow: ArrowBackOutline,
    forwardArrow: ArrowForwardOutline,
    backButton: Button,
    nextButton: Button,
    pageButton: Button,
};
const generatePageNumberArray = ({ numberOfPages, currentPage, numberOfSiblingButtons, }) => {
    // - If the current index is 1 or numberOfPages, push to the accumulator
    // - If the current index is the last index, push to the accumulator
    // - If the current index is within +-max siblings of the current page, push to the accumulator
    // - If the current index is not within +-max siblings of the current page, and the element at the previous index is not an ellipses, push an ellipsis to the accumulator
    const allPages = Array.from({ length: numberOfPages }, (_, index) => index + 1);
    const pages = allPages.reduce((acc, page) => {
        const prev = acc[acc.length - 1];
        if (page === 1 || page === numberOfPages) {
            acc.push({ type: "page", value: page });
        }
        else if (page === currentPage) {
            acc.push({ type: "page", value: page });
        }
        else if ((currentPage === 1 &&
            page >= currentPage - (numberOfSiblingButtons + 1) &&
            page <= currentPage + (numberOfSiblingButtons + 1)) ||
            (currentPage === numberOfPages &&
                page <= currentPage + (numberOfSiblingButtons + 1) &&
                page >= currentPage - (numberOfSiblingButtons + 1))) {
            acc.push({ type: "page", value: page });
        }
        else if (page >= currentPage - numberOfSiblingButtons &&
            page <= currentPage + numberOfSiblingButtons) {
            acc.push({ type: "page", value: page });
        }
        else if (prev?.type !== "ellipsis") {
            acc.push({ type: "ellipsis" });
        }
        return acc;
    }, []);
    return pages;
};
export const Pagination = (props) => {
    const { currentPage: _currentPage, variant = "small", numberOfPages = 1, numberOfSiblingButtons = 1, onPageChange, onBack, onNext, color = "surface", colorSet, invertColors, slots, slotProps, className, ...rest } = useDefaultProps({ props, component: "Pagination" });
    const density = variant === "large" ? "comfortable" : "dense";
    const [currentPage, setCurrentPage] = useState(_currentPage ?? 1);
    const [pages, setPages] = useState(generatePageNumberArray({
        numberOfPages,
        currentPage,
        numberOfSiblingButtons: numberOfSiblingButtons,
    }));
    const handleBack = () => {
        if (currentPage > 1) {
            onBack?.(currentPage - 1, currentPage);
            setCurrentPage(currentPage - 1);
        }
    };
    const handleNext = () => {
        if (currentPage < numberOfPages) {
            onNext?.(currentPage + 1, currentPage);
            setCurrentPage(currentPage + 1);
        }
    };
    useEffect(() => {
        setCurrentPage(_currentPage ?? 1);
    }, [_currentPage]);
    useEffect(() => {
        setPages(generatePageNumberArray({
            numberOfPages,
            currentPage,
            numberOfSiblingButtons: numberOfSiblingButtons,
        }));
    }, [currentPage, numberOfSiblingButtons, numberOfPages]);
    return (_jsx(SlotsProvider, { baseSlots: paginationSlots, slots: slots, children: ({ backArrow: BackArrow, forwardArrow: ForwardArrow, backButton: BackButton, nextButton: NextButton, pageButton: PageButton, }) => (_jsxs(Grid, { container: true, justifyContent: "center", gap: 2, className: clsx([paginationClasses.root, className]), ...rest, children: [currentPage > 1 && (_jsx(BackButton, { variant: "outlined", density: density, color: color, colorSet: colorSet, invertColors: invertColors, startNode: _jsx(BackArrow, { ...slotProps?.backArrow }), ...slotProps?.backButton, className: clsx([paginationClasses["back-button"], className]), sx: mergeSx({
                        alignSelf: "center",
                        flexShrink: 0,
                    }, slotProps?.backButton?.sx), onClick: (e) => {
                        handleBack();
                        slotProps?.backButton?.onClick?.(e);
                    }, children: "Back" })), pages.map((page, index) => {
                    return (_jsx(PageButton
                    // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
                    , { variant: "outlined", density: density, color: color, colorSet: colorSet, invertColors: invertColors, focused: page.type === "page" && page.value === currentPage, readonly: page.type === "ellipsis", ...slotProps?.pageButton, className: clsx([paginationClasses["page-button"], className]), onClick: (e) => {
                            if (page.type === "page") {
                                onPageChange?.(page.value, currentPage);
                                setCurrentPage(page.value);
                                slotProps?.pageButton?.onClick?.(e);
                            }
                        }, children: page.type === "page" ? page.value : "…" }, index));
                }), currentPage < numberOfPages && (_jsx(NextButton, { variant: "outlined", density: density, color: color, colorSet: colorSet, invertColors: invertColors, endNode: _jsx(ForwardArrow, { ...slotProps?.forwardArrow }), ...slotProps?.nextButton, className: clsx([paginationClasses["next-button"], className]), sx: mergeSx({
                        alignSelf: "center",
                        flexShrink: 0,
                    }, slotProps?.nextButton?.sx), onClick: (e) => {
                        handleNext();
                        slotProps?.nextButton?.onClick?.(e);
                    }, children: "Next" }))] })) }));
};
export default Pagination;
//# sourceMappingURL=Pagination.js.map